import { ActionContext, Module } from 'vuex';
import { getUserInfoProgress } from '@/api/homeV2Status';
import type { UserInfoProgress } from '@/api/homeV2Status/types';
import { UserInfoStatus } from '@/enums/UserInfoStatus';
import { ubtSendError } from '@/shared/utils/ubt';
import type { Store } from 'vuex';
import { StateType } from './types';
import RootStateType from '../types';
import generateMutations, { UPDATE_STATE_TYPE } from '../generate-mutations';

const moduleStore: Module<StateType, RootStateType> = {
  namespaced: true,
  state: {
    userInfoProgress: undefined,
    isAllProfileHasComplete: undefined,
    isPhaseOneProfileHasComplete: undefined,
  },
  actions: {
    /** 读取用户资料填写进度数据 */
    async getUserInfoProgress(
      context: ActionContext<StateType, RootStateType>,
    ): Promise<UserInfoProgress> {
      /** 请求新数据 */
      const userInfoProgress = await getUserInfoProgress();

      context.dispatch(UPDATE_STATE_TYPE, {
        userInfoProgress,
        isAllProfileHasComplete: userInfoProgress.fillInTheInformation === UserInfoStatus.Completed,
        isPhaseOneProfileHasComplete: userInfoProgress.firstInfoComplete === true,
      });

      return userInfoProgress;
    },

    /** 检查用户所有资料是否填写完成 */
    async checkAllProfileHasComplete(
      context: ActionContext<StateType, RootStateType>,
    ): Promise<boolean> {
      try {
        await context.dispatch('getUserInfoProgress');

        return !!context.state.isAllProfileHasComplete;
      } catch (error) {
        ubtSendError(error as Error);
        /** 若是接口异常，默认返回 false，中断流程 */
        return false;
      }
    },

    /**
     * 检查用户第一阶段的资料是否填写完成（极简-前两步：ocr、活体）
     */
    async checkPhaseOneProfileHasComplete(
      context: ActionContext<StateType, RootStateType>,
    ): Promise<boolean> {
      try {
        await context.dispatch('getUserInfoProgress');

        return !!context.state.isPhaseOneProfileHasComplete;
      } catch (error) {
        ubtSendError(error as Error);
        /** 若是接口异常，默认返回 false，中断流程 */
        return false;
      }
    },
  },
};

export default generateMutations(moduleStore);

export const generateDispatchActions = (store: Store<RootStateType>): {
  getUserInfoProgress: () => Promise<UserInfoProgress>;
  checkAllProfileHasComplete: () => Promise<boolean>;
  checkPhaseOneProfileHasComplete: () => Promise<boolean>;
} => ({
  getUserInfoProgress: (): Promise<UserInfoProgress> => store.dispatch('userProfileProgress/getUserInfoProgress') as unknown as Promise<UserInfoProgress>,
  checkAllProfileHasComplete: (): Promise<boolean> => store.dispatch('userProfileProgress/checkAllProfileHasComplete') as unknown as Promise<boolean>,
  checkPhaseOneProfileHasComplete: (): Promise<boolean> => store.dispatch('userProfileProgress/checkPhaseOneProfileHasComplete') as unknown as Promise<boolean>,
});
